import { DataGridPremium, GridPagination, gridClasses } from "@mui/x-data-grid-premium";
import { styled } from '@mui/material/styles';
import TextField from '@mui/material/TextField';
import React, { useState, useMemo } from "react";

const hexToRgba = (hex, alpha) => {
  if (!hex) return 'transparent'
  const c = hex.replace('#', '')
  const r = parseInt(c.substr(0, 2), 16)
  const g = parseInt(c.substr(2, 2), 16)
  const b = parseInt(c.substr(4, 2), 16)
  return `rgba(${r},${g},${b},${alpha})`
}

const DataGridColorWrapper = styled(DataGridPremium)(({ theme }) => ({
  [`& .${gridClasses.row}.prio--10`]: {
    backgroundColor: '#533',
    '&:hover, &.Mui-hovered': {
      backgroundColor: '#644',
      '@media (hover: none)': {
        backgroundColor: 'transparent',
      },
    },
    '&.Mui-selected': {
      backgroundColor: '#544',
      '&:hover, &.Mui-hovered': {
        backgroundColor: '#655',
      },
    },
  },
  [`& .${gridClasses.row}.prio--5`]: {
    backgroundColor: '#334',
    '&:hover, &.Mui-hovered': {
      backgroundColor: '#445',
      '@media (hover: none)': {
        backgroundColor: 'transparent',
      },
    },
    '&.Mui-selected': {
      backgroundColor: '#556',
      '&:hover, &.Mui-hovered': {
        backgroundColor: '#667',
      },
    },
  },
}));

const CustomPagination = (props) => {
  const { apiRef, ...other } = props;
  const [input, setInput] = useState('');
  const pageCount = Math.ceil((apiRef.current.state.rows.totalRowCount || 0) / (apiRef.current.state.pagination.paginationModel.pageSize || 1));

  const handleGo = () => {
    const page = parseInt(input, 10);
    if (page >= 1 && page <= pageCount) {
      apiRef.current.setPage(page - 1);
      setInput('');
    }
  };

  if (pageCount <= 1) return <GridPagination {...other} />;

  return (
    <div style={{ display: 'flex', alignItems: 'center' }}>
      <GridPagination {...other} />
      <TextField
        size="small"
        variant="standard"
        placeholder={`1-${pageCount}`}
        value={input}
        onChange={e => setInput(e.target.value.replace(/\D/g, ''))}
        onKeyDown={e => e.key === 'Enter' && handleGo()}
        sx={{ width: 60, mx: 1 }}
        inputProps={{ style: { textAlign: 'center' } }}
      />
    </div>
  );
};

const DataGrid = ({ apiRef, ...params }) => {

  const dynamicStyles = useMemo(() => {
    const rows = params.rows || []
    const seen = new Map()
    rows.forEach(row => {
      const color = row.priorityRef?.color
      const id = row.priorityRef?.id
      if (color && id && !seen.has(id)) seen.set(id, color)
    })
    if (!seen.size) return null
    let css = ''
    seen.forEach((color, id) => {
      const sel = `.${gridClasses.row}.prio-ref--${id}`
      css += `${sel}{background-color:${hexToRgba(color, 0.2)} !important}`
      css += `${sel}:hover,${sel}.Mui-hovered{background-color:${hexToRgba(color, 0.3)} !important}`
      css += `${sel}.Mui-selected{background-color:${hexToRgba(color, 0.25)} !important}`
      css += `${sel}.Mui-selected:hover,${sel}.Mui-selected.Mui-hovered{background-color:${hexToRgba(color, 0.35)} !important}`
    })
    return css
  }, [params.rows])

  return (
    <>
      {dynamicStyles && <style>{dynamicStyles}</style>}
      <DataGridColorWrapper
        getRowClassName={p => p.row.priorityRef?.id ? `prio-ref--${p.row.priorityRef.id}` : `prio--${p.row.priority || 0}`}
        apiRef={apiRef}
        cellSelection
        rowSelection={false}
        clipboardCopyCellDelimiter={'\t'}
        slots={{ pagination: CustomPagination }}
        slotProps={{ pagination: { apiRef } }}
        {...params}
      />
    </>
  )
}

export default DataGrid
